import {
  apiClient,
  createSuccessResponse,
  handleApiError,
} from "../../../../../service/api.js";
import { mapReportLinkApiToView } from "../lib/map-report-link-api-to-view.js";

/**
 * @class ReportLinksService
 * @description Serviço para endpoints de report-links.
 */
class ReportLinksService {
  constructor() {
    this.baseUrl = "/report-links";
  }

  /**
   * @method list
   * @param {Object} [params]
   * @param {RequestInit} [requestOptions]
   * @returns {Promise<Object>}
   */
  async list(params = {}, requestOptions) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.pageSize) queryParams.append("pageSize", params.pageSize);
      if (params.search) queryParams.append("search", params.search);
      if (params.isActive) queryParams.append("isActive", params.isActive);
      if (params.orderBy) queryParams.append("orderBy", params.orderBy);
      if (params.orderDirection) {
        queryParams.append("orderDirection", params.orderDirection);
      }

      const url = queryParams.toString()
        ? `${this.baseUrl}?${queryParams.toString()}`
        : this.baseUrl;

      const response = await apiClient.get(url, requestOptions);
      const rawData = response?.data?.data ?? response?.data ?? [];
      const data = Array.isArray(rawData)
        ? rawData.map(mapReportLinkApiToView)
        : [];
      const pagination = response?.data?.pagination ?? response?.pagination;

      return {
        ...createSuccessResponse(
          data,
          response?.status ?? 200,
          response?.message ?? "Links de relatório carregados com sucesso",
        ),
        pagination,
      };
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * @method findById
   * @param {string|number} id
   * @param {RequestInit} [requestOptions]
   * @returns {Promise<Object>}
   */
  async findById(id, requestOptions) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}`, requestOptions);
      const item = response?.data ?? response;
      return createSuccessResponse(
        mapReportLinkApiToView(item),
        response?.status ?? 200,
        response?.message ?? "Link de relatório encontrado com sucesso",
      );
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * @method create
   * @param {{ name: string, link: string, permissionKey?: string }} payload
   * @returns {Promise<Object>}
   */
  async create(payload) {
    try {
      const response = await apiClient.post(this.baseUrl, payload);
      const item = response?.data ?? response;
      return createSuccessResponse(
        mapReportLinkApiToView(item),
        response?.status ?? 201,
        response?.message ?? "Link de relatório criado com sucesso",
      );
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * @method update
   * @param {{{ name: string, link: string, permissionKey?: string, permissionId?: number }}} payload
   * @returns {Promise<Object>}
   */
  async update(payload) {
    try {
      const response = await apiClient.put(this.baseUrl, payload);
      const item = response?.data ?? response;
      return createSuccessResponse(
        mapReportLinkApiToView(item),
        response?.status ?? 200,
        response?.message ?? "Link de relatório atualizado com sucesso",
      );
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * @method delete
   * @param {string|number} id
   * @returns {Promise<Object>}
   */
  async delete(id) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${id}`);
      return createSuccessResponse(
        response?.data ?? response,
        response?.status ?? 200,
        response?.message ?? "Link de relatório removido com sucesso",
      );
    } catch (error) {
      return handleApiError(error);
    }
  }
}

const reportLinksService = new ReportLinksService();

export default reportLinksService;
